/**
 * Hex <-> HSL-triple conversion for per-tenant branding. The app's CSS
 * custom properties (see index.css) store bare "H S% L%" triples, not hex
 * or hsl(...) — Tailwind wires them as hsl(var(--primary)) etc. A tenant's
 * primaryColor arrives as "#RRGGBB" from the API and must be converted
 * before it can override those variables at runtime.
 */

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

function normalizeHex(hex: string): string | null {
  const match = HEX_RE.exec(hex.trim())
  const h = match?.[1]
  if (!h) return null
  return h.length === 3
    ? h
        .split('')
        .map((c) => c + c)
        .join('')
    : h
}

/** "#RRGGBB" | "#RGB" (with or without leading #) -> bare "H S% L%" triple,
 *  or null if malformed. */
export function hexToHslTriple(hex: string): string | null {
  const full = normalizeHex(hex)
  if (!full) return null

  const r = parseInt(full.slice(0, 2), 16) / 255
  const g = parseInt(full.slice(2, 4), 16) / 255
  const b = parseInt(full.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  const l = (max + min) / 2

  let h = 0
  let s = 0
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      default:
        h = (r - g) / d + 4
    }
    h /= 6
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/** WCAG relative luminance (0..1) of a normalized 6-digit hex string. */
function relativeLuminance(full: string): number {
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  const r = lin(parseInt(full.slice(0, 2), 16) / 255)
  const g = lin(parseInt(full.slice(2, 4), 16) / 255)
  const b = lin(parseInt(full.slice(4, 6), 16) / 255)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

const WHITE = '0 0% 100%'
const INK = '217 60% 14%' // matches --foreground in index.css (#0E2138)
const INK_LUMINANCE = 0.0212 // relative luminance of #0E2138

/** Contrast-safe foreground triple for text/glyphs rendered on top of
 *  [hex]. Picks whichever of white / deep ink has the higher contrast
 *  ratio, so it stays legible for an arbitrary tenant color. Falls back to
 *  white for malformed input. */
export function foregroundTripleForHex(hex: string): string {
  const full = normalizeHex(hex)
  if (!full) return WHITE
  const lBg = relativeLuminance(full)
  const contrastWhite = 1.05 / (lBg + 0.05)
  const contrastInk = (lBg + 0.05) / (INK_LUMINANCE + 0.05)
  return contrastWhite >= contrastInk ? WHITE : INK
}

/** Soft brand tint for active-nav pill backgrounds: same hue, high
 *  lightness, so dark ink text stays readable regardless of the source
 *  hue. Returns null for malformed input. */
export function softTripleFromHex(hex: string): string | null {
  const triple = hexToHslTriple(hex)
  if (!triple) return null
  const [h, s] = triple.split(' ')
  if (h === undefined || s === undefined) return null
  const sat = Math.min(parseInt(s, 10), 90)
  return `${h} ${sat}% 94%`
}
